import DOMPurify from "dompurify";

/**
 * Story text blocks are authored in a contentEditable, so `block.html` is
 * whatever the browser (and anything the author pasted) produced. It is
 * persisted verbatim, frozen at publish and rendered into the reader and the
 * print page — including at `/s/<slug>`, which anonymous readers open on this
 * app's own origin. Treat it as untrusted at every render.
 *
 * The allow-list is deliberately narrow: what the editor's toolbar can produce
 * (bold / italic / link) plus the block structure a paste of prose carries.
 * No `style`, no `class`, no images, no embedded content.
 */
const ALLOWED_TAGS = [
  "p",
  "br",
  "div",
  "span",
  "b",
  "strong",
  "i",
  "em",
  "u",
  "a",
  "ul",
  "ol",
  "li",
  "blockquote",
];

const ALLOWED_ATTR = ["href", "title"];

/**
 * The only link schemes an author may write. Anything else — notably
 * `javascript:` and `data:` — has its `href` dropped entirely.
 */
export const ALLOWED_URI_REGEXP = /^(?:https?|mailto):/i;

let hookInstalled = false;

function installHook() {
  if (hookInstalled) return;
  hookInstalled = true;
  // Story links open outside the reader; never hand the opener over.
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node instanceof Element && node.tagName === "A" && node.hasAttribute("href")) {
      node.setAttribute("target", "_blank");
      node.setAttribute("rel", "noopener noreferrer nofollow");
    }
  });
}

/** Sanitize authored story HTML for rendering. Always call this before `dangerouslySetInnerHTML`. */
export function sanitizeStoryHtml(html: string | null | undefined): string {
  if (!html) return "";
  installHook();
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOWED_URI_REGEXP,
    ADD_ATTR: ["target", "rel"],
  });
}

/**
 * Validate a URL typed into the link prompt. Returns the URL to use, or null
 * when the scheme isn't one we allow — a `javascript:` href typed here would
 * otherwise survive into the published story.
 */
export function normaliseLinkUrl(input: string | null | undefined): string | null {
  const trimmed = (input || "").trim();
  if (!trimmed) return null;
  // A bare "example.com/x" is a relative path to the URL parser; the author
  // means a web address, so default it to https.
  const candidate = /^[a-z][a-z0-9+.-]*:/i.test(trimmed) ? trimmed : `https://${trimmed}`;
  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return null;
  }
  return ["http:", "https:", "mailto:"].includes(parsed.protocol) ? parsed.href : null;
}
